import * as Sentry from '@/lib/sentry-lazy';
import { supabase } from '@/integrations/supabase/client';
import { ErrorType, ErrorClassification, Breadcrumb, JourneyEvent, generateErrorFingerprint } from './error-types';
import { addLogEntry, LogLevel } from '../logging-utils';

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  userRole?: string;
  schoolId?: string;
  academicYear?: string;
  sessionId: string;
  url: string;
  userAgent: string;
  timestamp: string;
  breadcrumbs: Breadcrumb[];
  customData?: Record<string, any>;
}

export interface EnhancedErrorLog {
  // Error details
  error: {
    id: string;
    type: ErrorType;
    name: string;
    message: string;
    stack?: string;
    code?: string;
    statusCode?: number;
  };
  
  // User context
  user: {
    id?: string;
    email?: string;
    role?: string;
    schoolId?: string;
    permissions?: string[];
  };
  
  // Session context
  session: {
    id: string;
    duration: number;
    pageViews: number;
    actions: number;
  };
  
  // Application context
  application: {
    version: string;
    environment: string;
    academicYear?: string;
    feature?: string;
    component?: string;
  };
  
  // Technical context
  technical: {
    url: string;
    method?: string;
    userAgent: string;
    viewport: { width: number; height: number };
    screen: { width: number; height: number };
    memory?: { used: number; total: number };
    connection?: { effectiveType: string; downlink: number };
  };
  
  // Journey context
  journey: {
    breadcrumbs: Breadcrumb[];
    lastActions: JourneyEvent[];
    errorCount: number;
  };
  
  // Metadata
  metadata: {
    timestamp: string;
    fingerprint: string;
    tags: string[];
    custom: Record<string, any>;
  };
}

export class EnhancedErrorLogger {
  private static instance: EnhancedErrorLogger;
  private sessionStartTime: number;
  private pageViews: number = 0;
  private actions: number = 0;
  private errorCount: number = 0;
  private sessionId: string;
  
  private constructor() {
    this.sessionStartTime = Date.now();
    this.sessionId = this.generateSessionId();
  }
  
  static getInstance(): EnhancedErrorLogger {
    if (!EnhancedErrorLogger.instance) {
      EnhancedErrorLogger.instance = new EnhancedErrorLogger();
    }
    return EnhancedErrorLogger.instance;
  }
  
  incrementPageViews() {
    this.pageViews++;
  }
  
  incrementActions() {
    this.actions++;
  }
  
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  private getAppVersion(): string {
    // In a real app, this would come from package.json or environment variable
    return import.meta.env.VITE_APP_VERSION || '1.0.0';
  }
  
  private getEnvironment(): string {
    return import.meta.env.MODE || 'production';
  }
  
  private getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      memory: this.getMemoryInfo(),
      connection: this.getConnectionInfo()
    };
  }
  
  private getMemoryInfo(): { used: number; total: number } | undefined {
    // @ts-ignore - performance.memory is a non-standard API
    if (performance.memory) {
      return {
        // @ts-ignore
        used: Math.round(performance.memory.usedJSHeapSize / 1048576),
        // @ts-ignore
        total: Math.round(performance.memory.jsHeapSizeLimit / 1048576)
      };
    }
    return undefined;
  }
  
  private getConnectionInfo(): { effectiveType: string; downlink: number } | undefined {
    // @ts-ignore - navigator.connection is a non-standard API
    if (navigator.connection) {
      return {
        // @ts-ignore
        effectiveType: navigator.connection.effectiveType || 'unknown',
        // @ts-ignore
        downlink: navigator.connection.downlink || 0
      };
    }
    return undefined;
  }
  
  private getSessionDuration(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1000); // in seconds
  }
  
  async logError(
    error: Error | any,
    classification: ErrorClassification,
    context: Partial<ErrorContext>,
    additionalData?: Record<string, any>
  ): Promise<void> {
    this.errorCount++;
    
    const fingerprint = generateErrorFingerprint(error, classification);
    const browserInfo = this.getBrowserInfo();
    
    const enhancedLog: EnhancedErrorLog = {
      error: {
        id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: classification.type,
        name: error?.name || 'Error',
        message: error?.message || 'Unknown error',
        stack: error?.stack,
        code: error?.code,
        statusCode: error?.statusCode
      },
      user: {
        id: context.userId,
        email: context.userEmail,
        role: context.userRole,
        schoolId: context.schoolId
      },
      session: {
        id: this.sessionId,
        duration: this.getSessionDuration(),
        pageViews: this.pageViews,
        actions: this.actions
      },
      application: {
        version: this.getAppVersion(),
        environment: this.getEnvironment(),
        academicYear: context.academicYear,
        feature: additionalData?.feature,
        component: additionalData?.component
      },
      technical: {
        url: window.location.href,
        method: additionalData?.method,
        userAgent: browserInfo.userAgent,
        viewport: browserInfo.viewport,
        screen: browserInfo.screen,
        memory: browserInfo.memory,
        connection: browserInfo.connection
      },
      journey: {
        breadcrumbs: context.breadcrumbs || [],
        lastActions: additionalData?.lastActions || [],
        errorCount: this.errorCount
      },
      metadata: {
        timestamp: new Date().toISOString(),
        fingerprint,
        tags: this.generateTags(classification, additionalData),
        custom: additionalData?.custom || {}
      }
    };
    
    // Log to database
    await this.saveToDatabase(enhancedLog, classification);

    // Log to Sentry with classification context (PII scrubbed at SDK level)
    Sentry.withScope((scope) => {
      scope.setTag('error_category', classification.category);
      scope.setTag('error_severity', classification.severity);
      scope.setTag('error_type', classification.type);
      scope.setLevel(classification.severity === 'critical' ? 'fatal' : classification.severity === 'high' ? 'error' : 'warning');
      scope.setFingerprint([fingerprint]);
      if (additionalData?.feature) scope.setTag('feature', additionalData.feature);
      if (additionalData?.component) scope.setTag('component', additionalData.component);
      scope.setContext('journey', {
        breadcrumbs_count: enhancedLog.journey.breadcrumbs.length,
        error_count: enhancedLog.journey.errorCount,
      });
      Sentry.captureException(error instanceof Error ? error : new Error(String(error?.message || error)));
    });

    // Log to console in development
    if (this.getEnvironment() === 'development') {
      console.error('Enhanced Error Log:', enhancedLog);
    }

    // Also use existing logging system for backward compatibility
    await addLogEntry(
      LogLevel.ERROR,
      `error-tracking-${classification.category}`,
      error?.message || 'Unknown error',
      enhancedLog,
      context.userId,
      context.academicYear
    );
  }
  
  private generateTags(classification: ErrorClassification, additionalData?: Record<string, any>): string[] {
    const tags: string[] = [
      classification.type,
      classification.category,
      `severity:${classification.severity}`,
      this.getEnvironment()
    ];
    
    if (classification.isRetryable) {
      tags.push('retryable');
    }
    
    if (additionalData?.tags) {
      tags.push(...additionalData.tags);
    }
    
    return tags;
  }
  
  private async saveToDatabase(log: EnhancedErrorLog, classification: ErrorClassification): Promise<void> {
    try {
      const insertData = {
          error_id: log.error.id,
          error_type: log.error.type,
          error_name: log.error.name,
          error_message: log.error.message,
          error_stack: log.error.stack,
          error_code: log.error.code,
          status_code: log.error.statusCode,
          
          // User context
          user_id: log.user.id,
          user_email: log.user.email,
          user_role: log.user.role,
          school_id: log.user.schoolId,
          
          // Session info
          session_id: log.session.id,
          session_duration: log.session.duration,
          
          // Application context
          app_version: log.application.version,
          environment: log.application.environment,
          academic_year: log.application.academicYear,
          feature: log.application.feature,
          component: log.application.component,
          
          // Technical details
          url: log.technical.url,
          http_method: log.technical.method,
          user_agent: log.technical.userAgent,
          viewport: log.technical.viewport,
          screen_info: log.technical.screen,
          
          // Journey data
          breadcrumbs: log.journey.breadcrumbs,
          last_actions: log.journey.lastActions,
          error_count: log.journey.errorCount,
          
          // Metadata
          fingerprint: log.metadata.fingerprint,
          tags: log.metadata.tags,
          custom_data: log.metadata.custom,
          
          occurred_at: log.metadata.timestamp
        };
      
      console.log('📝 Attempting to save error to database:', {
        errorType: insertData.error_type,
        errorMessage: insertData.error_message,
        fingerprint: insertData.fingerprint
      });
      
      const { error } = await supabase
        .from('error_logs')
        .insert(insertData);
      
      if (error) {
        console.error('❌ Failed to save error to database:', error);
        // If database save fails, log to localStorage as fallback
        this.saveToLocalStorage(log);
      } else {
        console.log('✅ Error successfully saved to database');
      }
    } catch (err) {
      console.error('❌ Exception while saving to database:', err);
      // Fallback to localStorage
      this.saveToLocalStorage(log);
    }
  }
  
  private saveToLocalStorage(log: EnhancedErrorLog): void {
    try {
      const storageKey = 'magnetflow_error_logs';
      const existingLogs = localStorage.getItem(storageKey);
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      
      logs.push({
        ...log,
        savedAt: new Date().toISOString()
      });
      
      // Keep only last 50 errors in localStorage
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }
      
      localStorage.setItem(storageKey, JSON.stringify(logs));
    } catch (err) {
      // Ignore localStorage errors
    }
  }
  
  async logQuickError(
    error: Error | any,
    category: string,
    additionalData?: Record<string, any>
  ): Promise<void> {
    const { classifyError } = await import('./error-types');
    const classification = classifyError(error);
    
    await this.logError(
      error,
      classification,
      {},
      {
        category,
        ...additionalData
      }
    );
  }
}